import React from "react";

const RELOAD_FLAG_KEY = "chunk-reload-attempted";

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const err = error as Error;
  const message = err?.message || String(error);
  return (
    err?.name === "ChunkLoadError" ||
    /Loading chunk [\w.-]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /Unexpected token '<'/i.test(message)
  );
}

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
  alreadyTriedReload: boolean;
}

class ChunkErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, isChunkError: false, alreadyTriedReload: false };

  static getDerivedStateFromError(error: unknown): State {
    const isChunkError = isChunkLoadError(error);
    return {
      hasError: true,
      isChunkError,
      alreadyTriedReload: isChunkError && sessionStorage.getItem(RELOAD_FLAG_KEY) === "1",
    };
  }

  componentDidCatch(error: unknown) {
    if (isChunkLoadError(error)) {
      if (sessionStorage.getItem(RELOAD_FLAG_KEY) !== "1") {
        sessionStorage.setItem(RELOAD_FLAG_KEY, "1");
        window.location.reload();
      }
    } else {
      console.error(error);
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError && !this.state.alreadyTriedReload) {
        // A reload is already in flight (triggered in componentDidCatch) —
        // render nothing while the page refreshes.
        return null;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-white text-center px-4">
          <p className="text-lg font-semibold text-gray-900 mb-2">
            {this.state.isChunkError ? "A new version of VouchLinks is available" : "Something went wrong"}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            {this.state.isChunkError
              ? "Please refresh the page to continue."
              : "Please refresh the page. If this keeps happening, contact support."}
          </p>
          <button
            onClick={() => {
              sessionStorage.removeItem(RELOAD_FLAG_KEY);
              window.location.reload();
            }}
            className="bg-black text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-gray-900 transition-colors"
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
